/**
 * Module-availability tracker. Polls master's `/v1/modules` endpoint and
 * exposes a reactive set of module keys with at least one connected client.
 * Consumers gate UI affordances on `isAvailable(key)` so apps that talk to
 * a disconnected module (e.g. calendar ↔ calendar) become unclickable.
 */

import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { readShellAccountId } from '@blpde/connect/auth';
import { buildAuthHeaders, getApiOrigin, hasAuthCredentials } from '@/auth';

interface ApiModule {
  uuid: string;
  key: string;
  connectedClients: number;
}

const REFRESH_INTERVAL_MS = 20_000;

const connected = ref<Set<string>>(new Set());
const loaded = ref(false);
let timer: ReturnType<typeof setInterval> | null = null;
let refcount = 0;
let inflight: Promise<void> | null = null;
let requestVersion = 0;
let snapshotOrigin = '';
let identity = '';
const currentIdentity = () => JSON.stringify([getApiOrigin(), readShellAccountId(), hasAuthCredentials()]);
const cacheKey = (origin: string) => `formica.connect.modules.v1:${encodeURIComponent(origin)}`;

function restoreSnapshot(origin: string): void {
  if (snapshotOrigin === origin) return;
  snapshotOrigin = origin;
  loaded.value = false;
  connected.value = new Set();
  try {
    const keys: unknown = JSON.parse(localStorage.getItem(cacheKey(origin)) || 'null');
    if (Array.isArray(keys) && keys.every((key) => typeof key === 'string')) {
      connected.value = new Set(keys);
      loaded.value = true;
    }
  } catch { /* no usable availability snapshot */ }
}


async function fetchOnce(): Promise<void> {
  if (inflight) return inflight;
  if (!hasAuthCredentials()) {
    connected.value = new Set();
    loaded.value = true;
    return;
  }
  identity = currentIdentity();
  const origin = getApiOrigin();
  const version = requestVersion;
  restoreSnapshot(origin);
  inflight = (async () => {
    try {
      const res = await fetch(`${origin}/v1/modules`, {
        headers: buildAuthHeaders({ Accept: 'application/json' }),
      });
      if (version !== requestVersion) return;
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          connected.value = new Set();
          try { localStorage.removeItem(cacheKey(origin)); } catch { /* storage unavailable */ }
        }
        return;
      }
      const data = (await res.json()) as { modules?: ApiModule[] };
      if (version !== requestVersion) return;
      const next = new Set<string>();
      for (const m of data.modules ?? []) {
        if (m && typeof m.key === 'string' && (m.connectedClients ?? 0) > 0) {
          next.add(m.key);
        }
      }
      connected.value = next;
      try { localStorage.setItem(cacheKey(origin), JSON.stringify([...next])); } catch { /* best effort */ }
      loaded.value = true;
    } catch {
      // Leave the previous snapshot in place — transient network errors
      // shouldn't blink every app icon to "unavailable".
    } finally {
      // Even a first offline startup must leave the checking screen. Cached
      // availability mounts the app; its own cache and API errors handle data.
      if (version === requestVersion) {
        loaded.value = true;
        inflight = null;
      }
    }
  })();
  return inflight;
}

function credentialsChanged(): void {
  requestVersion++;
  inflight = null;
  // A refreshed bearer is the same workspace; do not tear down its routed
  // editor while the availability request is in flight.
  if (identity !== currentIdentity()) {
    connected.value = new Set();
    loaded.value = false;
  }
  identity = currentIdentity();
  void fetchOnce();
}

function start() {
  if (refcount === 0) {
    window.addEventListener('formica:auth-changed', credentialsChanged);
    void fetchOnce();
    timer = setInterval(() => {
      void fetchOnce();
    }, REFRESH_INTERVAL_MS);
  }
  refcount += 1;
}

function stop() {
  refcount = Math.max(0, refcount - 1);
  if (refcount === 0 && timer) {
    window.removeEventListener('formica:auth-changed', credentialsChanged);
    clearInterval(timer);
    timer = null;
  }
}

export function useModuleAvailability() {
  onMounted(start);
  onBeforeUnmount(stop);

  const isAvailable = (moduleKey: string): boolean => {
    if (moduleKey === 'connect') return true;
    // While we haven't heard back yet, optimistically treat everything as
    // available — otherwise the first paint would disable every app for
    // ~one network round-trip.
    if (!loaded.value) return true;
    return connected.value.has(moduleKey);
  };

  return {
    loaded: computed(() => loaded.value),
    isAvailable,
    refresh: fetchOnce,
  };
}
